import React, { createContext, useContext, useState, useCallback } from 'react';

// Types
export type ObjectType = "SMALL_MOLECULE" | "BIOLOGIC" | "PEPTIDE" | "PROTEIN" | "UNKNOWN";

export type ObjectIdentity = {
  objectId: string;
  objectType: ObjectType;
  displayName: string;
  canonicalSmiles?: string;
  inchiKey?: string;
  mw?: number;
  formula?: string;
  chemblId?: string;
  pubchemCid?: string;
};

export type DomainKey = "PATENTS" | "TRIALS" | "BIOACTIVITY" | "PROTEINS" | "REACTIONS";
export type LensKey = "MAP_2D" | "EXPLORER_3D" | "OVERLAP" | "HEATMAP" | "TABLE";

export type StageBadge = "APPROVED" | "P3" | "P2" | "P1" | "IN_TRIALS" | "NOT_IN_TRIALS" | null;

export type DomainSummary = {
  patentCount: number;
  trialCount: number;
  targetCount: number;
  reactionCount: number;
  stage: StageBadge;
  stageTooltip?: string;
};

export type NavigationState = {
  // Object (immutable anchor)
  object: ObjectIdentity | null;

  // Domain (what aspect)
  domain: DomainKey;

  // Lens (how visualized)
  lens: LensKey;

  // Selection state
  selectedNodeId: string | null;
  selectedNodeIds: string[];

  // Domain summaries (badge counts)
  summary: DomainSummary;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Preferences
  showMiniStructure: boolean;
};

// Domain configuration
export const DOMAIN_CONFIG: Record<DomainKey, {
  label: string;
  description: string;
  defaultLens: LensKey;
  supportedLenses: LensKey[];
}> = {
  PATENTS: {
    label: "Patents",
    description: "Patent Space",
    defaultLens: "MAP_2D",
    supportedLenses: ["MAP_2D", "EXPLORER_3D", "OVERLAP", "TABLE"]
  },
  TRIALS: {
    label: "Trials",
    description: "Clinical Trials",
    defaultLens: "TABLE",
    supportedLenses: ["TABLE", "MAP_2D", "OVERLAP"]
  },
  BIOACTIVITY: {
    label: "Bioactivity",
    description: "Bioactivity Data",
    defaultLens: "MAP_2D",
    supportedLenses: ["MAP_2D", "HEATMAP", "TABLE", "OVERLAP"]
  },
  PROTEINS: {
    label: "Proteins",
    description: "Protein Network",
    defaultLens: "MAP_2D",
    supportedLenses: ["MAP_2D", "TABLE"]
  },
  REACTIONS: {
    label: "Reactions",
    description: "Synthesis Routes",
    defaultLens: "TABLE",
    supportedLenses: ["TABLE", "MAP_2D"]
  }
};

export const LENS_CONFIG: Record<LensKey, {
  label: string;
  description: string;
}> = {
  MAP_2D: { label: "Map", description: "2D Similarity Map" },
  EXPLORER_3D: { label: "3D", description: "3D Explorer" },
  OVERLAP: { label: "Overlap", description: "Venn Diagram" },
  HEATMAP: { label: "Heatmap", description: "Density View" },
  TABLE: { label: "Table", description: "Detail Table" }
};

interface NavigationContextType {
  state: NavigationState;

  // Actions
  setObject: (obj: ObjectIdentity) => void;
  clearObject: () => void;
  setDomain: (domain: DomainKey) => void;
  setLens: (lens: LensKey) => void;
  setSelection: (nodeId: string | null) => void;
  setSummary: (summary: Partial<DomainSummary>) => void;
  toggleMiniStructure: () => void;

  // Computed
  getMicrocopy: () => string;
  getContextHint: () => string | null;
  isSupportedLens: (lens: LensKey) => boolean;
}

const defaultState: NavigationState = {
  object: null,
  domain: "PATENTS",
  lens: "MAP_2D",
  selectedNodeId: null,
  selectedNodeIds: [],
  summary: {
    patentCount: 0,
    trialCount: 0,
    targetCount: 0,
    reactionCount: 0,
    stage: null
  },
  isLoading: false,
  error: null,
  showMiniStructure: true
};

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<NavigationState>(defaultState);

  const setObject = useCallback((obj: ObjectIdentity) => {
    setState(prev => ({
      ...prev,
      object: obj,
      // Reset to default domain/lens when new object scanned
      domain: "PATENTS",
      lens: "MAP_2D",
      selectedNodeId: null,
      selectedNodeIds: []
    }));
  }, []);

  const clearObject = useCallback(() => {
    setState(defaultState);
  }, []);

  const setDomain = useCallback((domain: DomainKey) => {
    setState(prev => {
      const config = DOMAIN_CONFIG[domain];
      // Keep current lens if supported, otherwise use domain default
      const newLens = config.supportedLenses.includes(prev.lens)
        ? prev.lens
        : config.defaultLens;

      return {
        ...prev,
        domain,
        lens: newLens,
        // Clear selection on domain change (v1 rule)
        selectedNodeId: null,
        selectedNodeIds: []
      };
    });
  }, []);

  const setLens = useCallback((lens: LensKey) => {
    setState(prev => {
      // Guard: only allow supported lenses for current domain
      const supported = DOMAIN_CONFIG[prev.domain].supportedLenses.includes(lens);
      if (!supported) return prev;

      return {
        ...prev,
        lens
        // Keep selection on lens change
      };
    });
  }, []);

  const setSelection = useCallback((nodeId: string | null) => {
    setState(prev => ({
      ...prev,
      selectedNodeId: nodeId,
      selectedNodeIds: nodeId ? [nodeId] : []
    }));
  }, []);

  const setSummary = useCallback((summary: Partial<DomainSummary>) => {
    setState(prev => ({
      ...prev,
      summary: { ...prev.summary, ...summary }
    }));
  }, []);

  const toggleMiniStructure = useCallback(() => {
    setState(prev => ({
      ...prev,
      showMiniStructure: !prev.showMiniStructure
    }));
  }, []);

  // Computed: "You are here" microcopy
  const getMicrocopy = useCallback(() => {
    if (!state.object) {
      return "Enter a molecule to begin scanning external databases.";
    }
    const domainLabel = DOMAIN_CONFIG[state.domain].description;
    const lensLabel = LENS_CONFIG[state.lens].description;
    return `You are viewing ${domainLabel} for this molecule using the ${lensLabel}.`;
  }, [state.object, state.domain, state.lens]);

  // Computed: Context hint line
  const getContextHint = useCallback(() => {
    const { trialCount, patentCount, targetCount } = state.summary;

    if (trialCount === 0 && patentCount > 0 && targetCount === 0) {
      return "Patent-only signal";
    }
    if (trialCount === 0 && targetCount > 0) {
      return "Bioactivity evidence present; no clinical signal";
    }
    if (trialCount === 0 && patentCount === 0 && targetCount === 0) {
      return "No external signal detected at current threshold";
    }
    return null;
  }, [state.summary]);

  const isSupportedLens = useCallback((lens: LensKey) => {
    return DOMAIN_CONFIG[state.domain].supportedLenses.includes(lens);
  }, [state.domain]);

  return (
    <NavigationContext.Provider value={{
      state,
      setObject,
      clearObject,
      setDomain,
      setLens,
      setSelection,
      setSummary,
      toggleMiniStructure,
      getMicrocopy,
      getContextHint,
      isSupportedLens
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};
